import {
  Box,
  ClickAwayListener,
  CircularProgress,
  InputBase,
  Paper,
  Popper,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import SearchIcon from '@mui/icons-material/Search'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useEdition } from '@/hooks/useEdition'
import { globalSearch, type SearchResult, type SearchResultType } from '@/services/globalSearchService'

const GROUP_ORDER: SearchResultType[] = ['study', 'respondent', 'template']

const GROUP_ICON: Record<SearchResultType, typeof VideocamOutlinedIcon> = {
  study: VideocamOutlinedIcon,
  respondent: PersonOutlineIcon,
  template: ListAltOutlinedIcon,
}

/** Where a result sends the user when picked. */
function targetPath(r: SearchResult): string {
  switch (r.type) {
    case 'study':
      return r.status === 'draft' ? `/studies/new?draft=${r.id}` : `/studies/${r.id}`
    case 'respondent':
      return '/respondents'
    case 'template':
      return `/questionnaires/${r.id}/edit`
  }
}

export default function GlobalSearch() {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('dashboard')
  const { t: tCommon } = useTranslation('common')
  const navigate = useNavigate()
  const { isPro } = useEdition()

  const anchorRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<SearchResult[]>([])
  const [active, setActive] = useState(0)

  const trimmed = query.trim()

  // Debounced search. A monotonically increasing request id guards against
  // out-of-order responses overwriting a fresher query's results.
  const reqId = useRef(0)
  useEffect(() => {
    if (trimmed.length < 2) {
      setResults([])
      setLoading(false)
      return
    }
    setLoading(true)
    const id = ++reqId.current
    const handle = window.setTimeout(async () => {
      const found = await globalSearch(trimmed, { includeProOnly: isPro })
      if (reqId.current !== id) return
      setResults(found)
      setActive(0)
      setLoading(false)
    }, 250)
    return () => window.clearTimeout(handle)
  }, [trimmed, isPro])

  // Flatten groups in display order so arrow keys can walk a single index.
  const ordered = useMemo(() => {
    return GROUP_ORDER.flatMap((type) => results.filter((r) => r.type === type))
  }, [results])

  const grouped = useMemo(() => {
    return GROUP_ORDER.map((type) => ({
      type,
      items: results.filter((r) => r.type === type),
    })).filter((g) => g.items.length > 0)
  }, [results])

  const select = (r: SearchResult) => {
    navigate(targetPath(r))
    setOpen(false)
    setQuery('')
    inputRef.current?.blur()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false)
      inputRef.current?.blur()
      return
    }
    if (!ordered.length) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((i) => (i + 1) % ordered.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => (i - 1 + ordered.length) % ordered.length)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const pick = ordered[active]
      if (pick) select(pick)
    }
  }

  const showPanel = open && trimmed.length > 0

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ display: { xs: 'none', md: 'block' }, position: 'relative' }}>
        <Box
          ref={anchorRef}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.75,
            py: 1,
            bgcolor: c.gray50,
            borderRadius: 34,
            width: 260,
            border: `1px solid ${showPanel ? c.gray200 : 'transparent'}`,
            transition: 'border-color 120ms ease',
          }}
        >
          <SearchIcon sx={{ fontSize: 16, color: c.ink48 }} />
          <InputBase
            inputRef={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setOpen(true)
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            // FaceWise searches studies only — promising respondents in the
            // placeholder would advertise a room this edition does not have.
            placeholder={isPro ? t('topbar.searchPlaceholder') : tCommon('searchStudiesPlaceholder')}
            sx={{ flex: 1, fontSize: 13 }}
          />
        </Box>

        <Popper
          open={showPanel}
          anchorEl={anchorRef.current}
          placement="bottom-end"
          style={{ zIndex: 1300 }}
          modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
        >
          <Paper
            elevation={0}
            sx={{
              width: { xs: 'calc(100vw - 32px)', sm: 360 },
              maxWidth: '100vw',
              maxHeight: 440,
              overflowY: 'auto',
              borderRadius: 2.5,
              border: `1px solid ${c.ink08}`,
              boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
              py: 0.5,
            }}
          >
            {loading && ordered.length === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 2, color: c.ink56, fontSize: 13 }}>
                <CircularProgress size={16} thickness={5} />
                {t('topbar.search.loading')}
              </Box>
            ) : ordered.length === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 2.5, color: c.ink56, fontSize: 13 }}>
                <HelpOutlineIcon sx={{ fontSize: 18, color: c.ink32 }} />
                {trimmed.length < 2 ? t('topbar.search.minChars') : t('topbar.search.empty', { query: trimmed })}
              </Box>
            ) : (
              grouped.map((group) => {
                const Icon = GROUP_ICON[group.type]
                return (
                  <Box key={group.type} sx={{ '&:not(:first-of-type)': { mt: 0.5 } }}>
                    <Box
                      sx={{
                        px: 2,
                        pt: 1,
                        pb: 0.5,
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 0.4,
                        textTransform: 'uppercase',
                        color: c.ink48,
                      }}
                    >
                      {t(`topbar.search.groups.${group.type}`)}
                    </Box>
                    {group.items.map((item) => {
                      const idx = ordered.indexOf(item)
                      const isActive = idx === active
                      return (
                        <Box
                          key={`${item.type}-${item.id}`}
                          onMouseEnter={() => setActive(idx)}
                          onMouseDown={(e) => {
                            // Keep focus on the input; fire before the click-away closes us.
                            e.preventDefault()
                            select(item)
                          }}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                            px: 2,
                            py: 1,
                            cursor: 'pointer',
                            bgcolor: isActive ? c.gray50 : 'transparent',
                          }}
                        >
                          <Box
                            sx={{
                              width: 30,
                              height: 30,
                              flex: '0 0 30px',
                              borderRadius: '8px',
                              bgcolor: c.gray100,
                              color: c.ink72,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Icon sx={{ fontSize: 16 }} />
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Box
                              sx={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: c.ink,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {item.title}
                            </Box>
                            {item.subtitle && (
                              <Box
                                sx={{
                                  fontSize: 12,
                                  color: c.ink56,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {item.subtitle}
                              </Box>
                            )}
                          </Box>
                          {item.type === 'study' && item.status && (
                            <Box
                              sx={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: item.status === 'draft' ? c.ink56 : theme.palette.success.main,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {t(`status.${item.status === 'draft' ? 'draft' : 'active'}`)}
                            </Box>
                          )}
                          {item.type === 'template' && item.meta && (
                            <Box sx={{ fontSize: 11, color: c.ink48, whiteSpace: 'nowrap' }}>
                              {t('topbar.search.questionCount', { count: Number(item.meta) })}
                            </Box>
                          )}
                        </Box>
                      )
                    })}
                  </Box>
                )
              })
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  )
}
